import { api } from './client';
import type { MyProfile } from '../types/api';

export interface AvatarUploadUrl {
  uploadUrl: string;
  key: string;
  bucket: string;
  expiresInSeconds: number;
  expiresAt: string;
  headers: Record<string, string>;
}

export async function requestAvatarUploadUrl(
  contentType: 'image/jpeg' | 'image/png' | 'image/webp',
  contentLength: number,
): Promise<AvatarUploadUrl> {
  const { data } = await api.post<AvatarUploadUrl>('/users/me/avatar/upload-url', {
    contentType,
    contentLength,
  });
  return data;
}

export async function confirmAvatar(key: string): Promise<MyProfile> {
  const { data } = await api.post<MyProfile>('/users/me/avatar/confirm', { key });
  return data;
}

export async function deleteAvatar(): Promise<MyProfile> {
  const { data } = await api.delete<MyProfile>('/users/me/avatar');
  return data;
}

export async function uploadAvatar(file: File): Promise<MyProfile> {
  const ct = file.type as 'image/jpeg' | 'image/png' | 'image/webp';
  if (!['image/jpeg', 'image/png', 'image/webp'].includes(ct)) {
    throw new Error('Only JPG, PNG, or WebP images are supported.');
  }
  if (file.size > 5 * 1024 * 1024) {
    throw new Error('Avatar must be under 5 MB.');
  }
  const presigned = await requestAvatarUploadUrl(ct, file.size);
  const headers: Record<string, string> = {};
  if (presigned.headers) {
    for (const [k, v] of Object.entries(presigned.headers)) {
      if (k.toLowerCase() !== 'content-length') {
        headers[k] = v;
      }
    }
  }
  if (!headers['Content-Type'] && !headers['content-type']) {
    headers['Content-Type'] = ct;
  }
  const putRes = await fetch(presigned.uploadUrl, {
    method: 'PUT',
    headers,
    body: file,
  });
  if (!putRes.ok) throw new Error(`Upload failed (${putRes.status})`);
  return confirmAvatar(presigned.key);
}
